/**
 * One-time invitation tokens may be present in a URL fragment (new links) or
 * in a legacy path segment.  Keep a pending token in session storage only
 * while an unauthenticated browser is sent through OIDC, then consume it as
 * soon as the invitation page is restored.
 */
export const PENDING_INVITATION_STORAGE_KEY = 'deploy_console_pending_invitation';
export const INVITATION_REDACTED_PATH = '/invitations';

type StorageLike = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;

export function invitationTokenFromLocation(pathname: string, hash: string): string {
  const fragmentToken = new URLSearchParams(hash.replace(/^#/, '')).get('token')?.trim();
  if (fragmentToken) {
    return fragmentToken;
  }

  const legacyMatch = /^\/invitations\/([^/]+)$/.exec(pathname);
  if (!legacyMatch) {
    return '';
  }
  try {
    return decodeURIComponent(legacyMatch[1]).trim();
  } catch {
    return '';
  }
}

export function storePendingInvitationToken(storage: StorageLike, token: string): void {
  if (token) {
    storage.setItem(PENDING_INVITATION_STORAGE_KEY, token);
  }
}

/** Consume rather than merely read so the plaintext token has a short lifetime. */
export function takePendingInvitationToken(storage: StorageLike): string {
  const token = storage.getItem(PENDING_INVITATION_STORAGE_KEY)?.trim() ?? '';
  storage.removeItem(PENDING_INVITATION_STORAGE_KEY);
  return token;
}

export function clearPendingInvitationToken(storage: StorageLike): void {
  storage.removeItem(PENDING_INVITATION_STORAGE_KEY);
}

/** The post-login destination deliberately contains no one-time token. */
export function pendingInvitationReturnPath(storage: StorageLike): string | null {
  return storage.getItem(PENDING_INVITATION_STORAGE_KEY)?.trim()
    ? INVITATION_REDACTED_PATH
    : null;
}
